import type { Outcome, SpokeWith, TouchChannel } from './enums';

/**
 * The Salesforce transfer (D91). Salesforce is Square's system of record and nothing here touches
 * it: this is a **mapping** the rep owns, which turns his own log into a block he can paste and a
 * CSV he can import. Which columns go out, their order, their headers and how our values map to
 * Salesforce's picklists are data, stored in `app_settings` and editable in the app, because the
 * fields Square actually requires are not known until after Nov 2. The *set of available fields* is
 * code — the app can only export what it records.
 */

/** Everything one touch can put in a column. Adding one is a code change; using one is not. */
export const EXPORT_FIELDS = [
  'subject',
  'company',
  'type',
  'status',
  'priority',
  'activity_date',
  'activity_time',
  'call_result',
  'spoke_with',
  'persona',
  'message_variant',
  'description',
  'next_step',
  'next_step_date',
  'contact_name',
  'phone',
  'email',
  'street',
  'city',
  'postal_code',
  'place_id',
  'lead_stage',
  'lead_source',
  'crm_lead_id',
  'crm_touch_id',
] as const;
export type ExportField = (typeof EXPORT_FIELDS)[number];

export interface ExportColumn {
  field: ExportField;
  /** The Salesforce column header this goes out under. */
  header: string;
  on: boolean;
}

/** Our value → the word Salesforce expects. A value with no entry goes out as our own label. */
export interface ExportValueMaps {
  channel?: Partial<Record<TouchChannel, string>>;
  outcome?: Partial<Record<Outcome, string>>;
  spoke_with?: Partial<Record<SpokeWith, string>>;
}

export interface ExportProfile {
  columns: ExportColumn[];
  values: ExportValueMaps;
  /** The end-of-day copy-paste block, one per touch. `{field}` tokens are the EXPORT_FIELDS above. */
  block: string;
  /** What the rep wrote about where these numbers came from, carried into the Lead Source column. */
  lead_source: string;
}

/**
 * Defaults shaped to Salesforce's standard Task object (Subject, Type, Status, Priority,
 * ActivityDate, Description, Call Result) with the fields a field rep actually fills. Every one of
 * them is a guess at Square's org until Artur sees it — which is exactly why they are editable.
 */
export const DEFAULT_EXPORT_PROFILE: ExportProfile = {
  columns: [
    { field: 'subject', header: 'Subject', on: true },
    { field: 'company', header: 'Company / Account', on: true },
    { field: 'type', header: 'Type', on: true },
    { field: 'status', header: 'Status', on: true },
    { field: 'priority', header: 'Priority', on: false },
    { field: 'activity_date', header: 'Due Date Only', on: true },
    { field: 'activity_time', header: 'Time', on: false },
    { field: 'call_result', header: 'Call Result', on: true },
    { field: 'spoke_with', header: 'Spoke With', on: true },
    { field: 'persona', header: 'Persona', on: false },
    { field: 'message_variant', header: 'Message Variant', on: false },
    { field: 'description', header: 'Comments', on: true },
    { field: 'next_step', header: 'Next Step', on: true },
    { field: 'next_step_date', header: 'Next Step Date', on: true },
    { field: 'contact_name', header: 'Contact', on: true },
    { field: 'phone', header: 'Phone', on: true },
    { field: 'email', header: 'Email', on: false },
    { field: 'street', header: 'Street', on: false },
    { field: 'city', header: 'City', on: true },
    { field: 'postal_code', header: 'Postal Code', on: false },
    { field: 'place_id', header: 'Place ID', on: false },
    { field: 'lead_stage', header: 'Stage', on: false },
    { field: 'lead_source', header: 'Lead Source', on: true },
    { field: 'crm_lead_id', header: 'CRM Lead Id', on: false },
    { field: 'crm_touch_id', header: 'CRM Touch Id', on: false },
  ],
  values: {
    // Salesforce's stock Task Type picklist is Call / Email / Meeting / Other
    channel: {
      walk_in: 'Other',
      call: 'Call',
      voicemail: 'Call',
      email: 'Email',
      linkedin: 'Other',
      meeting: 'Meeting',
      other: 'Other',
    },
    outcome: {
      interested: 'Interested',
      follow_up: 'Follow up',
      not_interested: 'Not interested',
      asked_to_return: 'Come back later',
      closed: 'Closed',
    },
    spoke_with: {
      owner: 'Owner',
      manager: 'Manager',
      gatekeeper: 'Gatekeeper',
      nobody: 'Nobody',
    },
  },
  block: `{company} — {type} on {activity_date}
Result: {call_result} · Spoke with: {spoke_with}
Next: {next_step} ({next_step_date})
{description}`,
  lead_source: 'Self-sourced — public records',
};

/** What the API would refuse, said in the form's own words before anything is sent — the same
 *  bounds as `api/src/routes/activity.ts` (a blank header is not a header). Returns the first
 *  problem with the field to put the caret in, or null when the profile is good. */
export const profileProblem = (
  profile: ExportProfile,
): { message: string; field: string } | null => {
  for (const column of profile.columns) {
    if (column.header.trim() === '')
      return { message: `Every column needs a name — "${column.field}" has none.`, field: column.field };
    if (column.header.length > 60)
      return { message: `"${column.header.slice(0, 20)}…" is too long for a column name (60).`, field: column.field };
  }
  for (const [group, map] of Object.entries(profile.values) as [string, Record<string, string>][]) {
    for (const [key, value] of Object.entries(map ?? {})) {
      if (value.trim() === '')
        return { message: `Every value needs a word — "${key}" has none.`, field: `${group}.${key}` };
      if (value.length > 60)
        return { message: `The word for "${key}" is too long (60).`, field: `${group}.${key}` };
    }
  }
  if (profile.block.trim() === '') return { message: 'The block to paste cannot be empty.', field: 'block' };
  if (profile.block.length > 2000) return { message: 'The block to paste is too long (2000).', field: 'block' };
  if (profile.lead_source.length > 120)
    return { message: 'The lead source is too long (120).', field: 'lead_source' };
  return null;
};

/** A stored profile may be older than the field list: unknown fields are dropped, new ones are
 *  appended switched off, so a deploy never silently changes what goes out. */
export const normalizeProfile = (stored: Partial<ExportProfile> | null | undefined): ExportProfile => {
  if (!stored) return DEFAULT_EXPORT_PROFILE;
  const known = new Set<string>(EXPORT_FIELDS);
  const kept = (stored.columns ?? []).filter((c) => known.has(c.field));
  const seen = new Set(kept.map((c) => c.field));
  const added = DEFAULT_EXPORT_PROFILE.columns
    .filter((c) => !seen.has(c.field))
    .map((c) => ({ ...c, on: false }));
  return {
    columns: kept.length ? [...kept, ...added] : DEFAULT_EXPORT_PROFILE.columns,
    values: { ...DEFAULT_EXPORT_PROFILE.values, ...(stored.values ?? {}) },
    block: stored.block ?? DEFAULT_EXPORT_PROFILE.block,
    lead_source: stored.lead_source ?? DEFAULT_EXPORT_PROFILE.lead_source,
  };
};
